import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { RootState } from "../../../store/Index";
import muzukurulogo from "../../../assets/muzukurublack.png";

interface B2BNavBarProps {
  navItems: { label: string; href: string }[];
  onDemoClick: () => void;
}

const B2BNavBar: React.FC<B2BNavBarProps> = ({ navItems, onDemoClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavItemClick = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const handleDemoClick = () => {
    setMobileMenuOpen(false);
    onDemoClick();
  };


  return (
    <>
      <Box
        component="nav"
        sx={{
          position: "sticky",
          top: 0,
          left: 0,
          width: "100%",
          height: { xs: "70px", md: "95px" },
          backdropFilter: "blur(8px) brightness(100%)",
          WebkitBackdropFilter: "blur(8px)",
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          //borderBottom: `1px solid ${colors.primary[800]}`,
          display: "flex",
          alignItems: "center",
          px: { xs: 2, sm: 4, lg: 8 },
          zIndex: 1200,
        }}
      >
        <Stack
          direction="row"
          sx={{ width: "100%", maxWidth: "1107px", margin: "0 auto" }}
          justifyContent="space-between"
          alignItems="center"
        >
          {/* Logo */}
          <Box
            component="img"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            src={muzukurulogo}
            alt="Muzukuru logo"
            sx={{
              width: { xs: "140px", sm: "160px", md: "161px" },
              height: "auto",
              cursor: "pointer",
            }}
          />

          {/* Desktop Navigation */}
          <Stack
            direction="row"
            spacing={{ sm: 3, md: 4 }}
            alignItems="center"
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {navItems.map((item) => (
              <Typography
                key={item.label}
                onClick={() => handleNavItemClick(item.href)}
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "black",
                  cursor: "pointer",
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#5D4037",
                  },
                }}
              >
                {item.label}
              </Typography>
            ))}

            <Button
              variant="contained"
              onClick={handleDemoClick}
              sx={{
                bgcolor: colors.primary[400],
                color: colors.primary[900],
                px: 3,
                py: 1.25,
                borderRadius: "50px",
                fontWeight: 700,
                fontSize: "0.875rem",
                textTransform: "none",
                boxShadow: `0 4px 14px ${colors.primary[400]}40`,
                "&:hover": {
                  bgcolor: colors.primary[500],
                },
              }}
            >
              Get a Demo
            </Button>
          </Stack>

          {/* Mobile Hamburger Menu */}
          <IconButton
            sx={{
              display: { xs: "flex", md: "none" },
              color: colors.primary[100],
            }}
            onClick={handleMobileMenuToggle}
            aria-label="menu"
          >
            <MenuIcon sx={{ fontSize: 32 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: { xs: "75%", sm: "300px" },
            maxWidth: "300px",
            backgroundColor: colors.primary[900],
            backdropFilter: "blur(8px)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Close Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <IconButton
              onClick={handleMobileMenuToggle}
              aria-label="close menu"
              sx={{ color: colors.primary[100] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Mobile Navigation Items */}
          <List>
            {navItems.map((item) => (
              <ListItem
                key={item.label}
                onClick={() => handleNavItemClick(item.href)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: `${colors.primary[400]}20`,
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontFamily: "Mulish",
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: colors.primary[100],
                  }}
                />
              </ListItem>
            ))}

            {/* Mobile Demo Button */}
            <ListItem sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleDemoClick}
                sx={{
                  bgcolor: colors.primary[400],
                  color: colors.primary[900],
                  py: 1.5,
                  borderRadius: "50px",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: colors.primary[500],
                  },
                }}
              >
                Get a Demo
              </Button>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default B2BNavBar;
